import { useState, useEffect, useCallback, useRef } from 'react';
import SearchIcon from '@mui/icons-material/Search';
import { TextField } from '@components/atoms/TextField';
import { SearchProps } from './Search.props';
import { useDebounce } from '@hooks/useDebounce';

export const Search = ({
  onSearch,
  debounceDelay = 500,
  placeholder = 'Search...',
  showSearchIcon = true,
  showClearButton = true,
  value: controlledValue,
  onChange,
  ...textFieldProps
}: SearchProps) => {
  const [searchValue, setSearchValue] = useState<string>(
    String(controlledValue ?? '')
  );

  const debouncedSearchValue = useDebounce(searchValue, debounceDelay);

  const onSearchRef = useRef(onSearch);
  const isMountedRef = useRef(false);

  useEffect(() => {
    onSearchRef.current = onSearch;
  }, [onSearch]);

  // Sync with controlled value if provided
  useEffect(() => {
    if (controlledValue !== undefined) {
      setSearchValue(String(controlledValue ?? ''));
    }
  }, [controlledValue]);

  // Trigger onSearch callback when debounced value changes
  useEffect(() => {
    // Skip the initial call on mount
    if (!isMountedRef.current) {
      isMountedRef.current = true;
      return;
    }

    if (onSearchRef.current) {
      onSearchRef.current(debouncedSearchValue);
    }
  }, [debouncedSearchValue]);

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = event.target.value;
      setSearchValue(newValue);

      // Call onChange if provided (for controlled components)
      if (onChange) {
        onChange(event);
      }
    },
    [onChange]
  );

  const handleClear = useCallback(() => {
    setSearchValue('');

    if (onSearchRef.current) {
      onSearchRef.current('');
    }
  }, []);

  return (
    <TextField
      {...textFieldProps}
      value={searchValue}
      onChange={handleChange}
      placeholder={placeholder}
      prefixIcon={showSearchIcon ? <SearchIcon /> : undefined}
      onClear={showClearButton ? handleClear : undefined}
      type="text"
    />
  );
};

export default Search;
